import {
  User, Mail, Cloud, Send, Link, Users, HelpCircle, Wrench,
  Bell, Calendar, CheckCircle, Clock, Database, Download,
  FileText, Filter, Flag, Folder, Globe, Heart,
  Key, Layers, Lock, MessageSquare, Monitor, Package,
  Phone, Play, Power, RefreshCw, Search, Server,
  Settings, Shield, Shuffle, Star, Tag, Terminal,
  ThumbsUp, Trash2, Upload, Zap, AlertTriangle, Archive,
  BarChart, Bookmark, Camera, Copy, Eye, GitBranch
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';

// eslint-disable-next-line react-refresh/only-export-components
export const AVAILABLE_ICONS: Record<string, { icon: LucideIcon; label: string }> = {
  'user': { icon: User, label: 'Användare' },
  'mail': { icon: Mail, label: 'E-post' },
  'cloud': { icon: Cloud, label: 'Moln' },
  'send': { icon: Send, label: 'Skicka' },
  'link': { icon: Link, label: 'Länk' },
  'users': { icon: Users, label: 'Grupp' },
  'help-circle': { icon: HelpCircle, label: 'Hjälp' },
  'wrench': { icon: Wrench, label: 'Verktyg' },
  'bell': { icon: Bell, label: 'Notis' },
  'calendar': { icon: Calendar, label: 'Kalender' },
  'check-circle': { icon: CheckCircle, label: 'Klar' },
  'clock': { icon: Clock, label: 'Tid' },
  'database': { icon: Database, label: 'Databas' },
  'download': { icon: Download, label: 'Ladda ner' },
  'file-text': { icon: FileText, label: 'Dokument' },
  'filter': { icon: Filter, label: 'Filter' },
  'flag': { icon: Flag, label: 'Flagga' },
  'folder': { icon: Folder, label: 'Mapp' },
  'globe': { icon: Globe, label: 'Webb' },
  'heart': { icon: Heart, label: 'Hjärta' },
  'key': { icon: Key, label: 'Nyckel' },
  'layers': { icon: Layers, label: 'Lager' },
  'lock': { icon: Lock, label: 'Lås' },
  'message-square': { icon: MessageSquare, label: 'Meddelande' },
  'monitor': { icon: Monitor, label: 'Skärm' },
  'package': { icon: Package, label: 'Paket' },
  'phone': { icon: Phone, label: 'Telefon' },
  'play': { icon: Play, label: 'Spela' },
  'power': { icon: Power, label: 'Ström' },
  'refresh-cw': { icon: RefreshCw, label: 'Uppdatera' },
  'search': { icon: Search, label: 'Sök' },
  'server': { icon: Server, label: 'Server' },
  'settings': { icon: Settings, label: 'Inställningar' },
  'shield': { icon: Shield, label: 'Säkerhet' },
  'shuffle': { icon: Shuffle, label: 'Blanda' },
  'star': { icon: Star, label: 'Stjärna' },
  'tag': { icon: Tag, label: 'Tagg' },
  'terminal': { icon: Terminal, label: 'Terminal' },
  'thumbs-up': { icon: ThumbsUp, label: 'Tumme upp' },
  'trash': { icon: Trash2, label: 'Radera' },
  'upload': { icon: Upload, label: 'Ladda upp' },
  'zap': { icon: Zap, label: 'Blixt' },
  'alert-triangle': { icon: AlertTriangle, label: 'Varning' },
  'archive': { icon: Archive, label: 'Arkiv' },
  'bar-chart': { icon: BarChart, label: 'Diagram' },
  'bookmark': { icon: Bookmark, label: 'Bokmärke' },
  'camera': { icon: Camera, label: 'Kamera' },
  'copy': { icon: Copy, label: 'Kopiera' },
  'eye': { icon: Eye, label: 'Visa' },
  'git-branch': { icon: GitBranch, label: 'Branch' },
};

// Map block types to default icon keys
// eslint-disable-next-line react-refresh/only-export-components
export const DEFAULT_BLOCK_ICONS: Record<string, string> = {
  'create-user': 'play',
  'disable-mailbox': 'message-square',
  'delete-m365': 'monitor',
  'send-email': 'bell',
  'webhook': 'zap',
  'add-to-group': 'users',
  'condition': 'git-branch',
  'general': 'settings',
};

// eslint-disable-next-line react-refresh/only-export-components
export function getBlockIcon(blockType: string, customIcon?: string): LucideIcon {
  const iconKey = customIcon || DEFAULT_BLOCK_ICONS[blockType] || 'wrench';
  return AVAILABLE_ICONS[iconKey]?.icon || Wrench;
}

interface IconPickerProps {
  currentIcon?: string;
  blockType: string;
  blockColor: string;
  onSelectIcon: (iconKey: string) => void;
}

export function IconPicker({ currentIcon, blockType, blockColor, onSelectIcon }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const activeKey = currentIcon || DEFAULT_BLOCK_ICONS[blockType] || 'wrench';
  const ActiveIcon = AVAILABLE_ICONS[activeKey]?.icon || Wrench;

  const handleSelect = (key: string) => {
    onSelectIcon(key);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="w-6 h-6 rounded-md flex items-center justify-center hover:scale-110 transition-transform cursor-pointer flex-shrink-0"
          style={{ backgroundColor: `${blockColor}20` }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title="Byt ikon"
        >
          <ActiveIcon className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-72 p-3 z-50 bg-popover border border-border shadow-xl" 
        side="right" 
        align="start"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <p className="text-xs font-medium text-muted-foreground mb-2">Välj ikon</p>
        <div className="grid grid-cols-7 gap-1 max-h-56 overflow-y-auto">
          {Object.entries(AVAILABLE_ICONS).map(([key, { icon: Icon, label }]) => (
            <button
              key={key}
              className={cn(
                "w-8 h-8 rounded-md flex items-center justify-center transition-colors",
                activeKey === key
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent text-foreground"
              )}
              onClick={() => handleSelect(key)}
              title={label}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
